// src/components/AugustToday.js
// August "Today" screen — warm gold port of ~/Downloads/magic-warm-today-journey.html (Today half).
import React, { useEffect, useRef, useState } from 'react'
import { DAYS, PRACTICES } from '../data/content'
import AugustTexture from './AugustTexture'
import DayRitualCore from './DayRitualCore'
import './AugustToday.css'

// Original lines — do NOT replace with the reference mockup's lyrics (copyrighted elsewhere);
// these specific lines are original copy, confirmed safe to port.
const WALLPAPER_LINES = [
  "Thank you for the slow coffee and the open window",
  "The metro pulled in right as I reached the platform",
  "Ten minutes of sun on the balcony before the call",
  "She laughed at my terrible joke and it fixed the day",
  "Rain on the awning while I wrote this down",
  "Day seven and it is starting to feel like a habit",
  "The bakery smell all the way down the street",
  "Grateful for the call I almost did not make",
  "The light in August is softer somehow",
  "Wrote three things and felt lighter after",
  "An old friend replied after all these months",
  "The first page of a new notebook still blank",
  "Someone held the door and it mattered today",
  "Cold water after the long walk home",
  "The song I forgot I loved came on again",
  "A little more patient than I was yesterday",
  "Dinner went long and nobody checked the time",
  "The plant on the sill finally opened up",
  "Wrote it down so I will not forget it",
  "One small thank you at a time",
]

const RINGS = [
  { width: 90, height: 90, top: -24, right: 26, opacity: 0.65 },
  { width: 66, height: 66, bottom: 120, left: -18, opacity: 0.45 },
]

const STATIC_SPLATS = [
  { width: 13, height: 11, top: 96, left: 20, opacity: 0.4, borderRadius: '60% 40% 55% 45%' },
  { width: 6,  height: 6,  top: 106, left: 36, opacity: 0.3, borderRadius: '50%' },
  { width: 11, height: 13, bottom: 150, right: 22, opacity: 0.36, borderRadius: '45% 55% 50% 50%' },
]

const CHECK_PATH = 'M5 13l4 4 10-11'

export default function AugustToday({ nextStation = 1, completedCount = 0, onOpenStation, onSeeAllDays, onOpenPractice, augustTheme, onAugustThemeChange }) {
  const reduced = useRef(
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  ).current

  const [show, setShow] = useState({
    cta: reduced, strip: reduced, whisper: reduced,
  })
  const [dotsIn, setDotsIn] = useState(reduced)
  const daysRef = useRef(null)
  const currentDotRef = useRef(null)

  // Keep the current day centered in the scrollable strip, on mount and whenever it changes.
  useEffect(() => {
    const container = daysRef.current
    const dot = currentDotRef.current
    if (!container || !dot) return
    const target = dot.offsetLeft - container.clientWidth / 2 + dot.clientWidth / 2
    container.scrollTo({ left: Math.max(0, target), behavior: reduced ? 'auto' : 'smooth' })
  }, [nextStation, reduced])

  useEffect(() => {
    if (reduced) return
    const timers = []
    const after = (ms, fn) => timers.push(setTimeout(fn, ms))
    after(900,  () => setShow(s => ({ ...s, cta: true })))
    after(1100, () => setShow(s => ({ ...s, strip: true })))
    after(1250, () => setDotsIn(true))
    after(2100, () => setShow(s => ({ ...s, whisper: true })))
    return () => timers.forEach(clearTimeout)
  }, [reduced])

  const day = DAYS.august?.[nextStation - 1]
  const trackName = day?.stationName || `Track ${nextStation}`
  // Prefers `short` over `full` (premium-refinement pass) — both are already-authored per day
  // (content.js), `short` is simply the tighter version. StationEntry.js computes this
  // independently (its own line, still prefers `full`) and is untouched by this swap — that
  // screen's own day-intro reading experience is out of scope here.
  const ritualText = day?.short || day?.full || "Write down what you're grateful for today."
  const isTodayDone = completedCount >= nextStation
  // Only surfaces on the exact day a Practice unlocks, and only if it actually has a built flow
  // (flow: null Practices are reachable from the library, but showing this module for one here
  // would dead-end the user straight off the Today screen).
  const todaysPractice = PRACTICES.find(p => p.unlockDay === nextStation && p.flow)
  // Real streak concept exists (calcCityStreak in Dashboard.js) but needs the full per-day
  // entries list, which this screen doesn't have — same completedCount proxy AugustJourney.js
  // uses, for the same reason (see its own comment on this exact tradeoff).
  const streak = completedCount

  return (
    <div className="aug-today">
      <AugustTexture variant="corner" wallpaperLines={WALLPAPER_LINES} rings={RINGS} splats={STATIC_SPLATS} lineCount={24} />

      <div className="aug-today-screen">
        <DayRitualCore
          dayNumber={nextStation}
          streak={streak}
          trackName={trackName}
          ritualText={ritualText}
          quote={day?.quote}
          descriptor="Today's Ritual"
          ctaLabel={isTodayDone ? 'Written today ✓' : 'Write 10 Gratitudes'}
          ctaDisabled={isTodayDone}
          ctaVisible={show.cta}
          onCta={onOpenStation}
          augustTheme={augustTheme} onAugustThemeChange={onAugustThemeChange}
        />

        {/* Secondary, optional layer below the mandatory 10 Gratitudes CTA — visually quieter
            than aug-today-cta on purpose (no solid fill, no shadow), so the hierarchy always
            reads "I only need my 10 Gratitudes today; this is here if I want to go deeper."
            Secondary line is a constant "new tool, yours to keep" framing, not
            todaysPractice.tagline — the day this Practice unlocks is, by design, the same day
            whose own Ritual theme it echoes (see PRACTICES.unlockDay in content.js), so the
            tagline restating that theme here reads as duplicating Ritual copy right above it.
            This line instead describes the card's ROLE (permanent, revisitable) rather than the
            Practice's content, which can't collide with any day's Ritual text. tagline itself is
            untouched — still used correctly by the Practices list row. */}
        {todaysPractice && (
          <div className={`aug-today-practice${show.cta ? ' show' : ''}`}>
            <div className="aug-today-practice-eyebrow">Today's Practice ✦</div>
            <div className="aug-today-practice-name">{todaysPractice.name}</div>
            <p className="aug-today-practice-tagline">New in your Magic Toolkit — yours to return to anytime.</p>
            <button className="aug-today-practice-cta" onClick={() => onOpenPractice(todaysPractice.id)}>
              Explore Practice →
            </button>
          </div>
        )}

        <button className={`aug-today-see-all${show.cta ? ' show' : ''}`} onClick={onSeeAllDays}>
          See all days
        </button>

        <div className={`aug-today-strip${show.strip ? ' show' : ''}`}>
          <div className="aug-today-strip-label">Your journey</div>
          <div className="aug-today-days" ref={daysRef}>
            {Array.from({ length: 28 }, (_, i) => {
              const status = i < completedCount ? 'done' : i === completedCount ? 'current' : 'next'
              return (
                <div
                  key={i}
                  ref={status === 'current' ? currentDotRef : null}
                  className={`aug-today-dot ${status}${dotsIn ? ' in' : ''}`}
                  style={{ transitionDelay: reduced ? '0ms' : `${i * 20}ms` }}
                >
                  {status === 'done'
                    ? <svg viewBox="0 0 24 24"><path d={CHECK_PATH} /></svg>
                    : i + 1}
                </div>
              )
            })}
          </div>
        </div>

        <div className={`aug-today-whisper${show.whisper ? ' show' : ''}`}>One track a day</div>
      </div>
    </div>
  )
}
